import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "./About.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// Generate a random avatar
const generateRandomAvatar = (name) => {
  const initials = name
    .split(" ")
    .map((word) => word[0])
    .join("");
  return `https://ui-avatars.com/api/?name=${initials}&background=000000&color=fff&size=128`;
};

// Convert star rating to numeric value
const getStarRating = (rating) => {
  const stars = ["ONE", "TWO", "THREE", "FOUR", "FIVE"];
  return stars.indexOf(rating.toUpperCase()) + 1;
};

// ReviewCard component
function ReviewCard({ review }) {
  if (!review) return null;
  const starCount = getStarRating(review.starRating || "");
  const avatar =
    review.reviewer && review.reviewer.displayName
      ? generateRandomAvatar(review.reviewer.displayName)
      : "https://via.placeholder.com/128?text=No+Avatar";
  const comment = review.comment || "";

  return (
    <div className="review-card">
      <div className="review-header">
        <img src={avatar} alt="Avatar" className="review-avatar" />
        <div className="review-content">
          <h5 className="reviewer-name">{review.reviewer?.displayName}</h5>
          <div className="review-stars">
            {[...Array(starCount)].map((_, i) => (
              <i key={i} className="bi bi-star-fill star-yellow"></i>
            ))}
            {[...Array(5 - starCount)].map((_, i) => (
              <i key={i + starCount} className="bi bi-star star-grey"></i>
            ))}
          </div>
        </div>
      </div>
      <div className="review-body">{comment}</div>
    </div>
  );
}

export default function About({ lang = "pt" }) {
  const [reviews, setReviews] = useState([]);
  const [current, setCurrent] = useState(0);
  const timerRef = useRef();

  useEffect(() => {
    async function fetchReviews() {
      const all = [];
      for (let i = 0; i < 20; i++) {
        try {
          const r = await import(`./reviews/reviews${i}.json`);
          if (Array.isArray(r.default)) all.push(...r.default);
          else if (r.default?.reviews) all.push(...r.default.reviews);
        } catch (e) {
          continue;
        }
      }
      setReviews(all);
    }

    fetchReviews();
  }, []);

  const filteredReviews = reviews.filter(
    (r) => getStarRating(r.starRating) >= 3 && r.comment && r.comment !== ""
  );

  useEffect(() => {
    if (filteredReviews.length === 0) return;
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % filteredReviews.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [filteredReviews.length]);

  return (
    <Container className="about-container">
      <Row>
        {/* LEFT COLUMN: About Us */}
        <Col md={6} className="about-section">
          <h2 className="section-title">
            {lang === "en" ? "About Us" : "Sobre Nós"}
          </h2>
          <div className="about-text">
            {lang === "en" ? (
              <>
                <p className="intro">
                  Our journey started with a dream and a deep{" "}
                  <strong>love for food</strong>. After immigrating from India,
                  our founder began working in an Italian restaurant in Germany,
                  where he discovered his <strong>passion for cooking</strong>{" "}
                  and blending <strong>bold flavors</strong>.
                </p>

                <p>
                  Years later, that passion brought us to the beautiful surf
                  town of <strong>Peniche, Portugal</strong>. Opening our
                  restaurant wasn’t easy—the first years were full of struggles.
                  But with <strong>consistency</strong>,{" "}
                  <strong>family sacrifice</strong>, and an obsession with{" "}
                  <strong>quality ingredients</strong>, we built something{" "}
                  <strong>special</strong>.
                </p>

                <p>
                  Today, we’re proud to welcome{" "}
                  <strong>surfers, travelers, and locals</strong> with food
                  that’s <strong>fast, fresh, and affordable</strong>. From our
                  famous <strong>Pizza Turca</strong> (Turkish Pizza Wrap) and
                  juicy <strong>doner kebabs</strong> to the{" "}
                  <strong>biggest pizzas in Peniche</strong>, every dish is
                  prepared with generous portions and flavor that speaks for
                  itself.
                </p>

                <p>
                  And the best part? You can enjoy it all with a{" "}
                  <strong>breathtaking view of the sea</strong>, right here in
                  the heart of Peniche’s surf paradise.
                </p>
              </>
            ) : (
              <>
                <p className="intro">
                  A nossa jornada começou com um sonho e um profundo{" "}
                  <strong>amor pela comida</strong>. Depois de emigrar da Índia,
                  o nosso fundador começou a trabalhar num restaurante italiano
                  na Alemanha, onde descobriu a sua{" "}
                  <strong>paixão pela cozinha</strong> e pela fusão de{" "}
                  <strong>sabores intensos</strong>.
                </p>

                <p>
                  Anos mais tarde, essa paixão trouxe-nos até à bela cidade
                  costeira de <strong>Peniche, Portugal.</strong> Abrir o nosso
                  restaurante não foi fácil — os primeiros anos foram cheios de
                  desafios. Mas com <strong>consistência</strong>,{" "}
                  <strong>sacrifício familiar</strong> e uma{" "}
                  <strong>dedicação</strong> absoluta a{" "}
                  <strong>ingredientes de qualidade</strong>, construímos algo{" "}
                  <strong>especial</strong>.
                </p>

                <p>
                  Hoje, temos orgulho em receber{" "}
                  <strong>surfistas, viajantes e locais</strong> com comida{" "}
                  <strong>rápida, fresca e acessível</strong>. Desde a nossa
                  famosa <strong>Pizza Turca</strong> (Wrap de Pizza Turca) e os
                  suculentos <strong>doner kebabs</strong>, até às{" "}
                  <strong>maiores pizzas de Peniche</strong>, cada prato é
                  preparado com generosas porções e sabores que falam por si.
                </p>

                <p>
                  E o melhor de tudo? Pode saborear tudo isto com uma{" "}
                  <strong>vista deslumbrante para o mar</strong>, bem no coração
                  do paraíso do surf em Peniche.
                </p>
              </>
            )}
          </div>
        </Col>

        {/* RIGHT COLUMN: Why Choose Us */}
        <Col md={6} className="about-section">
          <h3 className="section-title">
            {lang === "en"
              ? "Why Choose Us"
              : "Porque Escolher o Nosso Restaurante"}
          </h3>
          <div className="why-choose-us-container">
            {/* Top group */}
            <div className="why-choose-row">
              {(lang === "en"
                ? [
                    "🍕 Biggest Pizzas in Peniche – share with friends or tackle one yourself",
                    "⚡ Fast Service – perfect for surfers and travelers on the go",
                    "🌿 Fresh & Flavorful – only quality ingredients, cooked to order",
                  ]
                : [
                    "🍕 As maiores pizzas de Peniche – ideais para partilhar ou enfrentar sozinho",
                    "⚡ Serviço rápido – perfeito para surfistas e viajantes com pouco tempo",
                    "🌿 Fresco e saboroso – só ingredientes de qualidade, preparados no momento",
                  ]
              ).map((reason, idx) => (
                <div className="why-card" key={idx}>
                  {reason}
                </div>
              ))}
            </div>

            {/* Bottom group */}
            <div className="why-choose-row">
              {(lang === "en"
                ? [
                    "💰 Affordable Prices – eat well without breaking the bank",
                    "🌊 Sea View Dining – great food with an even better backdrop",
                  ]
                : [
                    "💰 Preços acessíveis – coma bem sem gastar muito",
                    "🌊 Vista para o mar – boa comida com um cenário ainda melhor",
                  ]
              ).map((reason, idx) => (
                <div className="why-card" key={idx}>
                  {reason}
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12} style={{ marginTop: 40, textAlign: "center" }}>
          <div className="slogan">
            <span className="slogan-separator">⸻</span>
            <div
              className="about-slogan-text"
              style={{
                fontWeight: 900,
                fontSize: "1.3rem",
                marginTop: 8,
              }}
            >
              {lang === "en"
                ? "Come Taste the Difference"
                : "Rápido. Saboroso. Honesto. Venha provar a diferença."}
            </div>
          </div>
        </Col>
      </Row>
      {/* FULL-WIDTH: What Our Clients Say */}
      <Row>
        <Col className="about-section" style={{ marginTop: "3rem" }}>
          <h3 className="section-title text-center">
            {lang === "en"
              ? "What Our Clients Say"
              : "O Que Dizem os Nossos Clientes"}
          </h3>
          <div className="review-slider">
            {filteredReviews.length === 0 ? (
              <div
                style={{
                  color: "#888",
                  fontStyle: "italic",
                  textAlign: "center",
                }}
              >
                {lang === "en" ? "No reviews yet." : "Ainda sem avaliações."}
              </div>
            ) : (
              <ReviewCard review={filteredReviews[current]} />
            )}

            {filteredReviews.length > 1 && (
              <div className="review-slider-dots">
                {filteredReviews.map((_, idx) => (
                  <span key={idx} />
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}
